import React, {useMemo, useState} from "react";
import {arrayPush, arrayRemoveIndex, arrayUpdate, arrayUpdatePartial, ArrayUpdateType} from "../../immutableState";
import SelectNumber from "../../components/SelectNumber";
import {classNames} from "../../wrapper";
import Input from "../../components/Input";
import TestType from "../../controllers/TestType";
import PrepareSubmissionLeaf from "../../controllers/PrepareSubmissionLeaf";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import Cultivar from "../../controllers/Cultivar";
import {useValidationArray} from "../validationArray";
import OrganType from "../../controllers/OrganType";
import SelectNumberNullable from "../../components/SelectNumberNullable";
import GenericSelect from "../../components/GenericSelect";
import NumberNullable from "../../components/NumberNullable";
import {pasteInColumn} from "../pasteColumn";
import {emptyLeafRow} from "./SubmissionPrepare";
import TableCore, {CountDisplay, TableColumn} from "../../components/TableCore";

export function getLabel(t: OrganType): string {
    switch (t) {
        case OrganType.Vegetative:
            return "ITEST Leaf (Vegetative)";
        case OrganType.Reproductive:
            return "ITEST Leaf (Reproductive)";
        case OrganType.Normal:
            return "ITEST Leaf";
        default:
            return "None";
    }
}

interface LeafRow extends PrepareSubmissionLeaf {
    selected: boolean;
}

const SubmissionTable: React.FC<{
    cultivars: Record<number, Cultivar[]>
    leafs: LeafRow[]
    data: SubmissionDataResponse<PrepareSubmissionLeaf>
    setData: (data: LeafRow[]) => void
    index: number
    registerValidation?: (index: number, validate: () => boolean) => void;
}> = (props) => {
    
    const [headerSampleType, setHeaderSampleType] = useState<OrganType>(OrganType.None);
    const [headerCrop, setHeaderCrop] = useState(0);
    const [headerCultivar, setHeaderCultivar] = useState<number[]>([]);
    const [headerTest, setHeaderTest] = useState<TestType>(TestType.StandardItestLeaf);
    const [headerOptionalTests, setHeaderOptionalTests] = useState<TestType[]>([]);
    const [split , setSplit] = useState<boolean>(false)

    function lengthCheck(length: number, deleted: boolean) {
        // if it's deleted, it's fine and the validation should pass
        return length > 0 || deleted;
    }

    function duplicatesampleNumberCheck(sampleNumber: string) {
        return props.leafs.filter(l => l.sampleNumber === sampleNumber).length === 1;
    }

    const validation = useValidationArray(props.leafs)({
        sampleNumber: leaf => lengthCheck(leaf.sampleNumber.length, leaf.delete) && duplicatesampleNumberCheck(leaf.sampleNumber),
        block: leaf => lengthCheck(leaf.blockNumber.length, leaf.delete),
        crop: leaf => lengthCheck(leaf.cropId, leaf.delete),
        cultivar: leaf => lengthCheck(leaf.cultivars.length, leaf.delete),
        growthPhase: leaf => lengthCheck(leaf.growthStage.length, leaf.delete),
        organType: leaf => leaf.organTypeEnum !== OrganType.None || leaf.delete,
        test: leaf => (leaf.test != null && leaf.test != TestType.Unknown) || leaf.delete,
        latitude: leaf => leaf.delete || leaf.latitude == null || (leaf.latitude >= -90 && leaf.latitude <= 90),
        longitude: leaf => leaf.delete || leaf.longitude == null || (leaf.longitude >= -180 && leaf.longitude <= 180),
    })

    props.registerValidation?.(props.index, () => validation.validate());

    function setLeafDataIndex(index: number, update: ArrayUpdateType<LeafRow>) {
        props.setData(arrayUpdatePartial( props.leafs, index, update))
    }
    
    function setLeafData(update: (leaf: LeafRow) => LeafRow) {
        const leafs = props.leafs;
        props.setData(leafs.map((l, i) => {
            return update(l)
        }))
    }

    function addCultivar(leaf: LeafRow, leafIndex: number): void {
        // select the first one from the list
        const list = props.cultivars[leaf.cropId] ?? [];
        if (list.length > 0)
            setLeafDataIndex(leafIndex, {cultivars: arrayPush(leaf.cultivars, 0)});
    }

    function updateHeaderCrop(cropId: number) {
        setHeaderCrop(cropId);
        setHeaderCultivar([])

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, cropId: cropId, cultivars: []};
        })
    }
    function updateHeader<T>(value: T, setHeader: (value: T) => void, property: keyof LeafRow) {
        setHeader(value);
        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, [property]: value};
        })
    }

    function changeHeaderTest(test: TestType) {
        updateHeader(test, setHeaderTest, 'test');
    }

    function updateHeaderSampleType(sampleDepth: OrganType) {
        updateHeader(sampleDepth, setHeaderSampleType, 'organTypeEnum');
    }

    function changeHeaderOptionalTest(optionalTests: TestType[]) {
        setHeaderOptionalTests(optionalTests);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, optionalTests: [...optionalTests]};
        })
    }

    function addHeaderCultivar() {
        // select the first one from the list
        const list = props.cultivars[headerCrop] ?? [];
        if (list.length > 0) {
            updateCultivarRows(arrayPush(headerCultivar, list[0]!.id))
        }
    }

    function changeHeaderCultivar(cultivarIndex: number, cultivarId: number) {
        updateCultivarRows(arrayUpdate(headerCultivar, cultivarIndex, cultivarId))
    }

    function removeHeaderCultivar(cultivarIndex: number) {
        updateCultivarRows(arrayRemoveIndex(headerCultivar, cultivarIndex))
    }

    function updateCultivarRows(cultivars: number[]) {
        setHeaderCultivar(cultivars)
        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, cultivars: cultivars};
        })
    }

    const allSelectedDeleted = props.leafs.some(l => l.selected) && props.leafs.filter(l => l.selected).every(l => l.delete);

    function toggleHeaderDelete() {
        setLeafData(l => l.selected ? {...l, delete: !allSelectedDeleted} : l);
    }

    // Selection: sync with row's selected property
    const selectedKeys = props.leafs
        .map((l, i) => ({selected: l.selected, index: i}))
        .filter(l => l.selected)
        .map(l => l.index);

    function handleSelectionChange(keys: number[]) {
        props.setData(props.leafs.map((l, i) => ({...l, selected: keys.includes(i)})));
        if (keys.length === 0) setSplit(false);
    }

    function removeLeaf(leafs: LeafRow[], index: number) {
        // this is only to remove leafs where the test data id is null (so if it is a manual submission and did not come from the capture app)
        props.setData(arrayRemoveIndex(leafs, index))
    }

    const columns: TableColumn<LeafRow>[] = useMemo(() => [
        {
            header: () => <div className="p-2 text-xs align-bottom">Sample Number</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({sampleNumber: v}), props.leafs, props.setData, emptyLeafRow)}
                            className={leafValidation?.rules.sampleNumber ? '' : 'border-1 border-red-600'}
                            value={item.sampleNumber}
                            change={v => setLeafDataIndex(index, {sampleNumber: v})}/>
                        {item.notes}
                    </div>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>
                        Sample Type
                    </div>
                    <SelectNumberNullable options={Object.values(OrganType)}
                                          textFunc={t => getLabel(t)}
                                          valueFunc={v => Object.values(OrganType).indexOf(v)}
                                          value={Object.values(OrganType).indexOf(headerSampleType)}
                                          onChange={v => updateHeaderSampleType(Object.values(OrganType)[v ?? 0] as OrganType)}/>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <SelectNumberNullable options={Object.values(OrganType)}
                                              className={leafValidation?.rules.organType ? '' : 'border-1 border-red-600'}
                                              textFunc={t => getLabel(t)}
                                              valueFunc={v => Object.values(OrganType).indexOf(v)}
                                              value={Object.values(OrganType).indexOf(item.organTypeEnum)}
                                              onChange={v => setLeafDataIndex(index, { organTypeEnum: Object.values(OrganType)[v ?? 0] })}/>
                    </div>
                );
            }
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Block</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({blockNumber: v}), props.leafs, props.setData, emptyLeafRow)}
                            className={leafValidation?.rules.block ? '' : 'border-1 border-red-600'}
                            value={item.blockNumber}
                            change={v => setLeafDataIndex(index, {blockNumber: v})}/>
                    </div>
                );
            }
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Latitude</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <NumberNullable value={item.latitude} change={v => setLeafDataIndex(index, {latitude: v})}>
                            {(attrs) => <input {...attrs}
                                disabled={item.testDataId !== null}
                                className={classNames('input', item.testDataId !== null ? 'bg-gray-200 cursor-not-allowed' : '', leafValidation?.rules.latitude ? '' : 'border-1 border-red-600')}
                                onPaste={e => pasteInColumn(e, index, v => ({latitude: v === '' ? null : isNaN(parseFloat(v)) ? null : parseFloat(v)}), props.leafs, props.setData, emptyLeafRow)}/>}
                        </NumberNullable>
                    </div>
                );
            }
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Longitude</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <NumberNullable value={item.longitude} change={v => setLeafDataIndex(index, {longitude: v})}>
                            {(attrs) => <input {...attrs}
                                disabled={item.testDataId !== null}
                                className={classNames('input', item.testDataId !== null ? 'bg-gray-200 cursor-not-allowed' : '', leafValidation?.rules.longitude ? '' : 'border-1 border-red-600')}
                                onPaste={e => pasteInColumn(e, index, v => ({longitude: v === '' ? null : isNaN(parseFloat(v)) ? null : parseFloat(v)}), props.leafs, props.setData, emptyLeafRow)}/>}
                        </NumberNullable>
                    </div>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Crop</div>
                    <SelectNumber options={props.data.crops} textFunc={c => c.name} valueFunc={c => c.id}
                                  value={headerCrop} onChange={v => updateHeaderCrop(v)}/>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <SelectNumber
                            className={leafValidation?.rules.crop ? '' : 'border-1 border-red-600'}
                            options={props.data.crops}
                            textFunc={c => c.name}
                            valueFunc={c => c.id}
                            value={item.cropId}
                            onChange={v => setLeafDataIndex(index, {cropId: v})}/>
                    </div>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Cultivar</div>
                    <div className="flex">
                        <div className="btn bg-primary my-1" onClick={() => addHeaderCultivar()}>
                            +
                        </div>
                        {headerCultivar.map((cultivar, cultivarIndex) => <div className="flex" key={cultivarIndex}>
                            <SelectNumber options={props.cultivars[headerCrop] ?? []}
                                          textFunc={c => c.name} valueFunc={c => c.id} value={cultivar}
                                          onChange={v => changeHeaderCultivar(cultivarIndex, v)}/>

                            <div className="m-1 text-red-500 cursor-pointer"
                                 onClick={() => removeHeaderCultivar(cultivarIndex)}>
                                x
                            </div>
                        </div>)}
                    </div>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <div className={"flex"}>
                            <div
                                className={classNames('btn my-1', leafValidation?.rules.cultivar ? 'bg-primary' : 'border-1 bg-red-600')}
                                onClick={() => addCultivar(item, index)}>
                                +
                            </div>
                            {item.cultivars.map((cultivar, cultivarIndex) => <div className="flex items-center" key={cultivarIndex}>
                                <SelectNumber options={props.cultivars[item.cropId] ?? []}
                                              className={cultivar > 0 ? '' : 'border-1 border-red-600'}
                                              textFunc={c => c.name} valueFunc={c => c.id}
                                              value={cultivar}
                                              onChange={v => setLeafDataIndex(index, {cultivars: arrayUpdate(item.cultivars, cultivarIndex, v)})}/>

                                <div className="m-1 text-red-500 cursor-pointer"
                                     onClick={() => setLeafDataIndex(index, {cultivars: arrayRemoveIndex(item.cultivars, cultivarIndex)})}>
                                    x
                                </div>
                            </div>)}
                        </div>
                    </div>
                );
            }
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Growth phase</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({growthStage: v}), props.leafs, props.setData, emptyLeafRow)}
                            className={leafValidation?.rules.growthPhase ? '' : 'border-1 border-red-600'}
                            value={item.growthStage}
                            change={v => setLeafDataIndex(index, {growthStage: v})}/>
                    </div>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Tests</div>
                    <GenericSelect
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerTest}
                        onChange={v => changeHeaderTest(v as TestType)}/>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <GenericSelect
                        className={leafValidation?.rules.test ? '' : 'border-1 border-red-600'}
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={item.test}
                        onChange={v => setLeafDataIndex(index, {test: v as TestType})}/>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Optional Tests</div>
                    <GenericSelect
                        options={props.data.optionalTests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerOptionalTests || []}
                        multiple={true}
                        onChange={v => changeHeaderOptionalTest(v as TestType[])}/>
                </div>
            ),
            row: (item, index) => (
                <GenericSelect
                    options={props.data.optionalTests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={item.optionalTests || []}
                    multiple={true}
                    onChange={v => setLeafDataIndex(index, {optionalTests: v as TestType[]})}/>
            )
        },
        {
            header: () => <div className="text-xs align-bottom text-center">
                <div>Actions</div>
                <div className={allSelectedDeleted ? "btn-sm btn-primary mx-auto" : "btn-sm btn-error mx-auto"} onClick={toggleHeaderDelete}>
                    {allSelectedDeleted ? '+' : '-'}
                </div>
            </div>,
            row: (item, index) => (
                <div className="table-data">
                    {item.delete ? <div className="btn-sm btn-primary"
                                        onClick={() => setLeafDataIndex(index, {delete: false})}>+</div> : null}
                    {!item.delete ? <div className="btn-sm btn-error"
                                         onClick={() => setLeafDataIndex(index, {delete: true})}>-</div> : null}
                    {item.testDataId === null ? (
                        <div className="btn-sm btn-error" onClick={() => removeLeaf(props.leafs, index)}>
                            x
                        </div>
                    ) : null}
                </div>
            )
        }
    ], [props.data, props.cultivars, headerSampleType, headerCrop, headerCultivar, headerTest, headerOptionalTests, validation.items]);

    return (
        <TableCore
            data={props.leafs}
            columns={columns}
            keyExtractor={(_, index) => index}
            headerClass="bg-gray-100"
            selection={{
                selectedKeys: selectedKeys,
                onSelectionChange: handleSelectionChange,
                selectAll: true,
                countDisplay: CountDisplay.CountOfTotal
            }}
        />
    );
}

export default SubmissionTable;
